import { createSelector } from 'reselect'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import { findTextElements } from 'helpers/googleSlides/shapes'
import featureFlagsSelectorCreator from 'redux/selectors/feature_flags/feature_flags_selector'
import sizeSelector from 'redux/selectors/ui/sizeSelector'

const featureFlagsSelector = featureFlagsSelectorCreator([
	featureFlagsTypes.READ_ONLY_IMMERSIVE_READER,
	featureFlagsTypes.GOOGLE_SLIDES_CLICKABLE_LINKS,
])

const getStudentViewData = state => state.student
const getLessonSessionData = state => state.lessonSession
const getSlideId = (state, props) => props.session.session_uid + '_' + props.slide.slideId

const buildSlideDataMetadata = textElements => (
	{
		payload: {
			pageElements: [{
				shape: {
					shapeType: 'TEXT_BOX',
					text: {
						textElements,
					},
					shapeProperties: {},
				},
				size: {
					width: {
						magnitude: 1024,
					},
					height: {
						magnitude: 768,
					},
				},
				transform: {
					scaleX: 1,
					scaleY: 1,
					translateX: 0,
					translateY: 0,
				},
			}],
		},
	}
)

const buildDefaultPageMetadata = () => (
	{
		pageSize: {
			width: {
				magnitude: 1024,
			},
			height: {
				magnitude: 768,
			},
		},
	}
)

const combinePageElementsIntoSingleElement = pageElements =>
	pageElements.reduce(
		(textsAccumulator, page) => {
			const textElements = 'shape' in page && findTextElements(page.shape)
			if (textElements) {
				textsAccumulator =
					textsAccumulator.concat(textElements)
				textsAccumulator.push('')
			}
			return textsAccumulator
		}
		, []
	)

const buildImmersiveReaderMetadata = metadata => {
	const slideMetadata = metadata.slide
	const pageElements = slideMetadata.payload.pageElements || []
	const textElements = combinePageElementsIntoSingleElement(pageElements)
	return {
		slide: buildSlideDataMetadata(textElements),
		page: metadata.page,
	}
}

const buildMetadata = (metadata, screenName) => {
	let slideMetadata
	if (metadata && metadata.slides[screenName]) {
		if (metadata.presentation) {
			slideMetadata = {
				page: metadata.presentation.payload,
				slide: metadata.slides[screenName].data,
			}
		} else {
			const slide = metadata.slides[screenName]
			const pageElements = slide.data.payload.pageElements || []
			const textElements = combinePageElementsIntoSingleElement(pageElements)
			const slideData = buildSlideDataMetadata(textElements)
			slideMetadata = {
				page: buildDefaultPageMetadata(),
				slide: slideData,
			}
		}
	}
	return slideMetadata
}

const getStudentSlideData = createSelector(
	[
		getStudentViewData,
		getSlideId,
		sizeSelector,
		getLessonSessionData,
		featureFlagsSelector,
	],
	(student, slideId, size, lessonSession, featureFlags) => {
		const slide_data = student.slides[slideId]
		let metadata = null
		let immersiveReader = null

		if (slide_data) {
			metadata = buildMetadata(
				lessonSession.metadata,
				slide_data.screenName,
			)
			if (metadata
				&& slide_data.shouldShowImmersiveReader
				&& featureFlags[featureFlagsTypes.READ_ONLY_IMMERSIVE_READER]) {
				immersiveReader = buildImmersiveReaderMetadata(metadata)
			}
		}

		return {
			slide_data,
			size,
			metadata,
			immersiveReader,
			canShowLinks: featureFlags[featureFlagsTypes.GOOGLE_SLIDES_CLICKABLE_LINKS],
		}
	}
)
export default getStudentSlideData

export {buildSlideDataMetadata, buildDefaultPageMetadata}
